// Coda locale persistente per le scritture verso FileMaker (orari/km/note uscita).
// Stesso pattern già usato e validato nell'app FileMaker Go: tabella locale di modifiche
// pendenti + tentativo di invio + pulizia dei record confermati alla riapertura.
import { openDB, IDBPDatabase } from 'idb';
import { updateUscita, UscitaWritableFields } from './api';

interface PendingWrite {
  id: string;
  recordId: number;
  fields: Partial<UscitaWritableFields>;
  tentativi: number;
  createdAt: number;
  lastError?: string;
  // modId noto al momento in cui l'utente ha fatto l'azione (non al momento dell'invio, che può
  // essere molto dopo se offline) — usato per rilevare conflitti, vedi sendOne/CLAUDE.md.
  expectedModId?: number;
}

// Scrittura scartata perché il record è stato modificato da qualcun altro nel frattempo (policy:
// vince il desk/admin — la scrittura in coda NON viene riprovata né riapplicata). Conservata per
// una futura notifica UI ("la tua modifica delle X non è stata salvata perché...").
export interface DiscardedConflict {
  id: string;
  recordId: number;
  fields: Partial<UscitaWritableFields>;
  discardedAt: number;
}

const DB_NAME = 'serpa-write-queue';
const STORE_NAME = 'pending_writes';
const DISCARDED_STORE_NAME = 'discarded_conflicts';
const DB_VERSION = 2;
const FLUSH_INTERVAL_MS = 30 * 1000;

let dbPromise: Promise<IDBPDatabase> | null = null;
let flushing = false;
let initialized = false;

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(DISCARDED_STORE_NAME)) {
          db.createObjectStore(DISCARDED_STORE_NAME, { keyPath: 'id' });
        }
      }
    });
  }
  return dbPromise;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

type SendResult = 'ok' | 'conflict' | 'retry';

async function sendOne(entry: PendingWrite): Promise<SendResult> {
  try {
    const res = await updateUscita(entry.recordId, entry.fields, entry.expectedModId);
    if (!res.error) return 'ok';
    // Il backend risponde 409 solo per MODID_CONFLICT (vedi uscite_endpoints.ts) — qualunque
    // altro errore (rete, token scaduto, 5xx) resta "retry", stesso comportamento di sempre.
    return res.status === 409 ? 'conflict' : 'retry';
  } catch {
    return 'retry';
  }
}

// Invia in coda una modifica e tenta subito un flush (fire-and-forget) — se fallisce per motivi
// transitori, resta persistita in IndexedDB e verrà ritentata dai trigger di flushQueue. Se fallisce
// per conflitto (modId non corrisponde più), NON viene ritentata — vedi sendOne/flushQueue.
// recordId è il recordId FM dell'uscita (uscita.recordId), non usc_id. expectedModId: passa
// uscita.modId così com'è al momento del click — la coda lo porta con sé fino all'invio reale.
export async function enqueueUscitaUpdate(
  recordId: number,
  fields: Partial<UscitaWritableFields>,
  expectedModId?: number
): Promise<void> {
  const db = await getDb();
  const entry: PendingWrite = {
    id: generateId(),
    recordId,
    fields,
    tentativi: 0,
    createdAt: Date.now(),
    expectedModId
  };
  await db.put(STORE_NAME, entry);
  void flushQueue();
}

// Notificato ogni volta che almeno una scrittura in coda va a buon fine — usato dal pattern
// manifest+cache (manifestSync.ts) per far scattare subito un sync incrementale invece di
// aspettare il prossimo giro periodico (vedi CLAUDE.md, trigger "subito dopo una scrittura
// riuscita"). writeQueue.ts non sa nulla di manifest/date range: notifica solo "qualcosa è
// cambiato", chi ascolta decide cosa farne.
type WriteSuccessListener = () => void;
const writeSuccessListeners = new Set<WriteSuccessListener>();

export function onWriteSuccess(listener: WriteSuccessListener): () => void {
  writeSuccessListeners.add(listener);
  return () => writeSuccessListeners.delete(listener);
}

function notifyWriteSuccess(): void {
  writeSuccessListeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.error('[writeQueue] Errore in un listener onWriteSuccess:', err);
    }
  });
}

// Svuota la coda tentando l'invio di ogni elemento pendente. Esiti:
// - 'ok': rimosso dalla coda, fatto.
// - 'conflict': rimosso dalla coda MA NON riapplicato — spostato in discarded_conflicts (policy
//   "vince il desk", vedi CLAUDE.md). L'utente ha perso quella specifica modifica: è la scelta
//   deliberata, non un bug.
// - 'retry': resta in coda con il contatore tentativi incrementato, ritentato al prossimo giro.
export async function flushQueue(): Promise<void> {
  if (flushing) return;
  flushing = true;
  let anySuccess = false;
  try {
    const db = await getDb();
    const all: PendingWrite[] = await db.getAll(STORE_NAME);
    for (const entry of all) {
      const result = await sendOne(entry);
      if (result === 'ok') {
        await db.delete(STORE_NAME, entry.id);
        anySuccess = true;
      } else if (result === 'conflict') {
        await db.delete(STORE_NAME, entry.id);
        const discarded: DiscardedConflict = {
          id: entry.id,
          recordId: entry.recordId,
          fields: entry.fields,
          discardedAt: Date.now()
        };
        await db.put(DISCARDED_STORE_NAME, discarded);
        console.warn(`[writeQueue] Scrittura scartata per conflitto (recordId ${entry.recordId}):`, entry.fields);
      } else {
        entry.tentativi += 1;
        await db.put(STORE_NAME, entry);
      }
    }
  } finally {
    flushing = false;
  }
  if (anySuccess) notifyWriteSuccess();
}

// Elenco delle scritture scartate per conflitto, non ancora "viste" dall'utente — da mostrare in
// una futura UI di notifica. Ordinate dalla più recente.
export async function getDiscardedConflicts(): Promise<DiscardedConflict[]> {
  const db = await getDb();
  const all: DiscardedConflict[] = await db.getAll(DISCARDED_STORE_NAME);
  return all.sort((a, b) => b.discardedAt - a.discardedAt);
}

// Da chiamare quando l'utente ha visto/riconosciuto le notifiche di conflitto.
export async function clearDiscardedConflicts(): Promise<void> {
  const db = await getDb();
  await db.clear(DISCARDED_STORE_NAME);
}

// Da chiamare una sola volta all'avvio dell'app: registra i trigger di retry (online, tab tornata
// visibile, intervallo periodico) e tenta subito uno svuotamento per eventuali modifiche rimaste
// in coda da una sessione precedente.
export function initWriteQueue(): void {
  if (initialized) return;
  initialized = true;

  window.addEventListener('online', () => { void flushQueue(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushQueue();
  });
  setInterval(() => { void flushQueue(); }, FLUSH_INTERVAL_MS);
  void flushQueue();
}
